import React, { useState } from 'react';

import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import useIsMobile from 'hooks/useIsMobile';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { userSelectorFamily } from 'recoil/login';

import { useEmailCertificationMutation, useVerifyRequestCodeMutation } from '../api';
import {
  Body,
  DarkButton,
  ErrorText,
  Field,
  Footer,
  InlineRow,
  Label,
  NextButton,
  RegHeader,
  RegShell,
  TextInput,
  ToastCard,
} from './mobileStyles';

function SignupDialog({ children }: React.PropsWithChildren) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [showCodeInput, setShowCodeInput] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [, setJwt] = useRecoilState(userSelectorFamily('jwt'));

  const goRegister = () => {
    setJwt('');
    navigate(`/register?email=${email}&loginType=email`);
  };

  const { mutate: emailCertificationMutation, isLoading: emailCertificationLoading } = useEmailCertificationMutation({
    onSuccess: () => {
      setShowCodeInput(true);
    },

    onError: (error) => {
      if (typeof error === 'string') {
        setEmailError(error);
      }
    },
  });

  const { mutate: verifyRequestCodeMutation, isLoading: verifyRequestCodeLoading } = useVerifyRequestCodeMutation({
    onSuccess: () => {
      setIsVerified(true);
      // 모바일: "다음" 하나로 인증+이동을 겸한다.
      if (isMobile) {
        goRegister();
      }
    },

    onError: (error) => {
      setCodeError(typeof error === 'string' ? error : '인증코드를 다시 확인해주세요.');
    },
  });

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsSubmitted(true);
    setEmailError(null);

    if (!email) {
      setEmailError('이메일을 입력하세요.');
      return;
    }

    if (showCodeInput) {
      goRegister();
      return;
    }

    emailCertificationMutation({ email });
  };

  const handleDialogOpenChange = () => {
    setEmail('');
    setCode('');
    setEmailError(null);
    setCodeError(null);

    setIsSubmitted(false);
    setIsVerified(false);
    setShowCodeInput(false);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setCode(value);
    setCodeError(null);
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) {
      setCodeError('인증 코드를 입력하세요.');
      return;
    }
    verifyRequestCodeMutation({ email, code });
  };

  // 모바일: 이메일 인증 요청
  const handleRequestEmail = () => {
    setIsSubmitted(true);
    setEmailError(null);
    if (!email) {
      setEmailError('이메일을 입력하세요.');
      return;
    }
    emailCertificationMutation({ email });
  };

  // 모바일: "다음" — 인증코드 검증 후(onSuccess) /register 이동
  const handleMobileNext = () => {
    if (!code) {
      setCodeError('인증 코드를 입력하세요.');
      return;
    }
    verifyRequestCodeMutation({ email, code });
  };

  return (
    <Dialog onOpenChange={handleDialogOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      {isMobile ? (
        <DialogContent className="!fixed !inset-0 !z-50 !h-[100dvh] !w-screen !max-w-none !translate-x-0 !translate-y-0 !gap-0 !rounded-none !border-0 !bg-white !p-0 !shadow-none [&>button]:hidden">
          <RegShell>
            <RegHeader as={DialogTitle}>회원가입</RegHeader>
            <Body>
              <Field>
                <Label>이메일</Label>
                <InlineRow>
                  <TextInput
                    type="email"
                    placeholder="이메일을 입력해주세요"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={showCodeInput}
                    $invalid={isSubmitted && !!emailError}
                  />
                  <DarkButton
                    type="button"
                    onClick={handleRequestEmail}
                    disabled={showCodeInput || emailCertificationLoading}
                  >
                    {emailCertificationLoading ? '요청 중' : '인증요청'}
                  </DarkButton>
                </InlineRow>
                {isSubmitted && emailError && <ErrorText>{emailError}</ErrorText>}
              </Field>

              {showCodeInput && (
                <Field>
                  <TextInput
                    type="text"
                    inputMode="numeric"
                    placeholder="이메일 인증코드"
                    value={code}
                    onChange={handleCodeChange}
                    $invalid={!!codeError}
                  />
                </Field>
              )}

              {showCodeInput && (
                <ToastCard>
                  {codeError ? (
                    codeError
                  ) : (
                    <>
                      인증코드가 이메일로 전송되었습니다.
                      <br />
                      이메일이 보이지 않는 경우, 스팸메일함을 확인해주세요.
                    </>
                  )}
                </ToastCard>
              )}
            </Body>
            <Footer>
              <NextButton
                type="button"
                $active={showCodeInput && !!code}
                disabled={!showCodeInput || !code || verifyRequestCodeLoading}
                onClick={handleMobileNext}
              >
                다음
              </NextButton>
            </Footer>
          </RegShell>
        </DialogContent>
      ) : (
        <DialogContent className="w-[420px] max-w-[95vw] rounded-2xl px-8 py-10 text-center">
          <DialogHeader className="flex flex-col items-center">
            <DialogTitle className="mb-1 text-[20px] font-bold">회원가입</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSignUp} className="flex flex-col">
            <div className="mb-6 text-left">
              <label htmlFor="signup-firstname" className="f mb-1 block text-sm font-medium">
                이메일
              </label>
              <Input
                id="signup-firstname"
                type="email"
                className="h-[48px] rounded-md border border-gray-200 px-4 text-base focus:ring-0"
                placeholder="이메일을 입력하세요"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="given-name"
                disabled={showCodeInput}
              />
              {isSubmitted && emailError && (
                <p id="signup-email-error" className="mt-2 text-xs text-primary">
                  {emailError}
                </p>
              )}
            </div>

            {showCodeInput && (
              <div className="mb-6 flex flex-col">
                <label htmlFor="signup-code" className="mb-2 block text-left text-sm font-semibold">
                  인증 코드
                </label>
                <div className="flex gap-2">
                  <Input
                    id="signup-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={8}
                    className="h-[48px] flex-1 rounded-md border border-gray-200 px-4 text-base focus:ring-0"
                    placeholder="코드를 입력하세요"
                    value={code}
                    onChange={handleCodeChange}
                    autoComplete="one-time-code"
                  />

                  <Button
                    type="button"
                    className="h-[48px] rounded-lg bg-primary px-6 text-base font-semibold text-white disabled:bg-gray-300"
                    onClick={handleVerifyCode}
                    disabled={isVerified || verifyRequestCodeLoading}
                  >
                    {verifyRequestCodeLoading ? '인증 중...' : '인증'}
                  </Button>
                </div>

                <p className="mt-2 text-left text-xs text-gray-500">
                  이메일로 발송된 인증코드를 확인해주세요. <br />
                  이메일이 보이지 않는다면 스팸메일함을 확인해주세요.
                </p>

                {isVerified && (
                  <p className="mt-2 flex items-center gap-1 text-left text-xs text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>인증 완료</span>
                  </p>
                )}
                {codeError && (
                  <p className="mt-2 flex items-center gap-1 text-left text-xs text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <span>{codeError}</span>
                  </p>
                )}
              </div>
            )}

            <DialogFooter>
              {showCodeInput ? (
                <Button
                  type="submit"
                  className="h-[52px] w-full cursor-pointer rounded-lg bg-primary text-base font-semibold text-white"
                  disabled={!isVerified}
                >
                  회원가입
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="h-[52px] w-full cursor-pointer rounded-lg bg-primary text-base font-semibold text-white"
                  disabled={emailCertificationLoading}
                >
                  {emailCertificationLoading ? '이메일 인증 중...' : '이메일 인증 요청'}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default SignupDialog;
